import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { boolean, index, integer, pgTable, text, timestamp, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { ChallengeToContributor } from './challenge-to-contributor'
import { ChallengeToLesson } from './challenge-to-lesson'
import { ChallengeToOrganization } from './challenge-to-organization'
import { ChallengeToTeam } from './challenge-to-team'
import { ChallengeToUser } from './challenge-to-user'

export const Challenge = pgTable(
  'challenge',
  {
    id: uuid().notNull().primaryKey(),
    title: text().notNull(),
    slug: text().notNull(),
    estimatedTimeMinutes: integer(),
    learningFocus: text().notNull(),
    coverKey: text().notNull(),
    backgroundImageKey: text().notNull(),
    eventName: text().notNull(),
    desktopSliderKey: text(),
    mobileSliderKey: text(),
    startDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    endDate: timestamp({ mode: 'date', withTimezone: true }).notNull(),
    isPublic: boolean().notNull().default(true),
    isActive: boolean().notNull().default(true),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(),
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`),

    // legacy
    legacyNeonChallengeId: text(),
  },
  (table) => [
    index('challenge_title_idx').on(table.title),
    index('challenge_slug_idx').on(table.slug),
    index('challenge_event_name_idx').on(table.eventName),
    index('challenge_start_date_idx').on(table.startDate),
    index('challenge_end_date_idx').on(table.endDate),
    index('challenge_is_public_idx').on(table.isPublic),
    index('challenge_is_active_idx').on(table.isActive),
    index('challenge_cover_key_idx').on(table.coverKey),
    index('challenge_background_image_key_idx').on(table.backgroundImageKey),
    index('challenge_desktop_slider_key_idx').on(table.desktopSliderKey),
    index('challenge_mobile_slider_key_idx').on(table.mobileSliderKey),
    index('challenge_updated_at_idx').on(table.updatedAt),
    index('challenge_created_at_idx').on(table.createdAt),
    index('challenge_legacy_neon_challenge_id_idx').on(table.legacyNeonChallengeId),
  ],
)

export const challengeRelations = relations(Challenge, ({ many }) => ({
  challengeToLessons: many(ChallengeToLesson),
  challengeToContributors: many(ChallengeToContributor),
  challengeToOrganizations: many(ChallengeToOrganization),
  challengeToTeams: many(ChallengeToTeam),
  challengeToUsers: many(ChallengeToUser),
}))

export const InsertChallengeSchema = createInsertSchema(Challenge)
export const UpdateChallengeSchema = createUpdateSchema(Challenge).omit({ id: true }).partial()
export const SelectChallengeSchema = createSelectSchema(Challenge)

export type InsertChallenge = typeof Challenge.$inferInsert
export type UpdateChallenge = z.infer<typeof UpdateChallengeSchema>
export type SelectChallenge = typeof Challenge.$inferSelect
